import { BaseElement } from 'base';
import { bus } from 'bus';
import Swiper from 'swiper-core';
import { MediaFloor, MEDIA_PLAYING_TOPIC } from 'media-floor';

const MUTED_TOPIC = 'media:muted';
const REDUCE = '(prefers-reduced-motion: reduce)';
const TOGGLE_TAG = 'action-button';
const VOLUME = 0.5;
const PLAY_STATES = ['paused', 'playing'];
const SOUND_STATES = ['unmuted', 'muted'];
const STALL_TIMEOUT = 4000;
const SLIDE_SPEED = 300;

class TiktokVideos extends BaseElement {
  #destroyed = false;
  #bound = false;
  #reduceMq = null;
  #track = null;
  #viewport = null;
  #cards = [];
  #cardsById = new Map();
  #videos = new Map();
  #playToggles = new Map();
  #soundToggles = new Map();
  #sourceCounts = new Map();
  #failedSources = new Map();
  #playable = new Set();
  #needsReload = new Set();
  #owner = null;
  #floor = new MediaFloor();
  #muted = false;
  #prev = null;
  #next = null;
  #swiper = null;
  #stallTimers = new Map();

  onConnect() {
    this.#track = this.q('[data-track]');
    this.#viewport = this.q('[data-viewport]');
    if (!this.#track || !this.#viewport) return;

    this.#destroyed = false;
    this.#bound = false;
    this.#reduceMq = window.matchMedia(REDUCE);

    this.#cards = this.qa('[data-card]');
    this.#cardsById = new Map(
      this.#cards.map(card => [card.dataset.mediaId, card])
    );

    this.#videos = new Map();
    for (const video of this.qa('[data-video]')) {
      this.#videos.set(this.#cardOf(video), video);
    }
    this.#playable = new Set(this.#videos.keys());
    this.#needsReload = new Set();

    this.#playToggles = new Map();
    for (const toggle of this.qa(`${TOGGLE_TAG}.tiktok-videos__play`)) {
      this.#playToggles.set(this.#cardOf(toggle), toggle);
    }

    this.#soundToggles = new Map();
    for (const toggle of this.qa(`${TOGGLE_TAG}.tiktok-videos__sound`)) {
      this.#soundToggles.set(this.#cardOf(toggle), toggle);
    }

    this.#prev = this.q(`${TOGGLE_TAG}.tiktok-videos__arrow--prev`);
    this.#next = this.q(`${TOGGLE_TAG}.tiktok-videos__arrow--next`);

    this.#sourceCounts = new Map();
    this.#failedSources = new Map();
    for (const source of this.qa('[data-video] source')) {
      const card = this.#cardOf(source);
      this.#sourceCounts.set(card, (this.#sourceCounts.get(card) ?? 0) + 1);
    }

    for (const video of this.#videos.values()) {
      video.volume = VOLUME;
      this.listen(video, 'error', this.#onMediaError, true);
      this.listen(video, 'play', this.#onVideoPlay);
      this.listen(video, 'playing', this.#onVideoPlaying);
      this.listen(video, 'pause', this.#onVideoPause);
      this.listen(video, 'stalled', this.#onStalled);
      this.listen(video, 'waiting', this.#onStalled);
    }

    this.subscribe(MEDIA_PLAYING_TOPIC, this.#onPlayingChanged);

    this.subscribe(MUTED_TOPIC, this.#onMutedChanged);

    this.listen(this.#viewport, 'keydown', this.#onKeydown);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);

    this.observe(
      new IntersectionObserver(this.#onSectionVisible.bind(this), {
        threshold: 0
      }),
      this.#viewport
    );

    this.#initSwiper();

    this.cleanup(() => {
      for (const timer of this.#stallTimers.values()) clearTimeout(timer);
      this.#stallTimers.clear();
    });

    customElements.whenDefined(TOGGLE_TAG).then(() => this.#bindToggles());
  }

  onDisconnect() {
    this.#destroyed = true;
    this.#bound = false;
    for (const video of this.#videos.values()) video.pause();
    this.#releaseFloor();
  }

  #bindToggles() {
    if (this.#destroyed || !this.isConnected) return;

    for (const [card, toggle] of this.#playToggles) {
      toggle.toggleIcon({
        states: PLAY_STATES,
        onChange: () => {
          this.#togglePlay(card);
        }
      });
      this.#markPlaying(card, this.#videos.get(card)?.paused === false);
    }

    for (const toggle of this.#soundToggles.values()) {
      toggle.toggleIcon({
        states: SOUND_STATES,
        onChange: () => {
          this.#toggleSound();
        }
      });
    }
    this.#syncSoundIcons();

    this.#prev?.run(() => {
      this.#step(-1);
    });
    this.#next?.run(() => {
      this.#step(1);
    });

    this.#bound = true;
    this.#syncEdges();
  }

  #togglePlay(card) {
    const video = this.#videos.get(card);
    if (!video || !this.#playable.has(card)) return;
    if (video.paused) this.#start(card);
    else this.#stop(card);
  }

  #start(card) {
    if (this.#destroyed || !this.isConnected) return;
    const video = this.#videos.get(card);
    if (!video) return;

    if (this.#needsReload.delete(card)) video.load();
    video.muted = this.#muted;
    video.volume = VOLUME;
    this.#floor.claim(card.dataset.mediaId);
    video.play().then(null, error => this.#onPlayRejected(error, card));
  }

  #stop(card) {
    this.#videos.get(card)?.pause();
    this.#releaseFloor(card);
  }

  #stopAll() {
    for (const video of this.#videos.values()) video.pause();
    this.#releaseFloor();
  }

  #releaseFloor(card) {
    this.#floor.release(card?.dataset.mediaId);
  }

  #onPlayingChanged(id) {
    this.#owner = id;
    for (const [card, video] of this.#videos) {
      if (card.dataset.mediaId !== id) video.pause();
    }
  }

  #onPlayRejected(error, card) {
    if (this.#destroyed || !this.isConnected) return;
    this.#markPlaying(card, false);

    if (error.name === 'AbortError') return;
    if (error.name === 'NotAllowedError') {
      this.#releaseFloor(card);
      return;
    }

    console.error('[tiktok-videos] playback error:', error);
    if (error.name === 'NotSupportedError') {
      this.#degrade(card);
      return;
    }
    this.#releaseFloor(card);
  }

  #onVideoPlay(event) {
    this.#markPlaying(this.#cardOf(event.target), true);
  }

  #onVideoPause(event) {
    const card = this.#cardOf(event.target);
    this.#clearStallTimer(card);
    this.#markPlaying(card, false);
  }

  #onVideoPlaying(event) {
    const card = this.#cardOf(event.target);
    if (!card) return;
    card.dataset.mediaStarted = '';
    this.#clearStallTimer(card);
    this.#needsReload.delete(card);
    this.#failedSources.delete(card);
    this.#markPlaying(card, true);
  }

  #markPlaying(card, isPlaying) {
    if (!card) return;
    card.toggleAttribute('data-playing', isPlaying);
    const play = this.#playToggles.get(card);
    play?.setIconState?.(isPlaying ? 'playing' : 'paused');
    if (isPlaying) return;

    const sound = this.#soundToggles.get(card);
    if (sound?.contains(document.activeElement)) play?.control?.focus();
  }

  #onMediaError(event) {
    const card = this.#cardOf(event.target);
    if (!card) return;

    if (event.target.tagName === 'SOURCE') {
      const failed = (this.#failedSources.get(card) ?? 0) + 1;
      this.#failedSources.set(card, failed);
      if (failed < (this.#sourceCounts.get(card) ?? 0)) return;
    } else if (!this.#isFatal(event.target.error)) {
      this.#recover(card);
      return;
    }

    if (card.dataset.mediaStarted === undefined) {
      this.#degrade(card);
      return;
    }
    this.#recover(card);
  }

  #isFatal(error) {
    return (
      error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
      error?.code === MediaError.MEDIA_ERR_DECODE
    );
  }

  #onStalled(event) {
    const card = this.#cardOf(event.target);
    if (!card || this.#stallTimers.has(card)) return;
    const video = event.target;
    const armedAt = video.currentTime;
    const timer = setTimeout(() => {
      this.#stallTimers.delete(card);
      if (this.#destroyed || !this.isConnected) return;
      if (video.paused || video.currentTime > armedAt) return;
      this.#recover(card);
    }, STALL_TIMEOUT);
    this.#stallTimers.set(card, timer);
  }

  #clearStallTimer(card) {
    const timer = this.#stallTimers.get(card);
    if (timer === undefined) return;
    clearTimeout(timer);
    this.#stallTimers.delete(card);
  }

  #recover(card) {
    this.#clearStallTimer(card);
    this.#failedSources.delete(card);
    this.#needsReload.add(card);
    this.#videos.get(card)?.pause();
    this.#markPlaying(card, false);
    this.#releaseFloor(card);
  }

  #degrade(card) {
    this.#clearStallTimer(card);
    card.dataset.mediaFailed = 'true';
    delete card.dataset.mediaStarted;
    this.#playable.delete(card);
    this.#needsReload.delete(card);
    this.#releaseFloor(card);
  }

  #onMutedChanged(muted) {
    this.#muted = Boolean(muted);
    for (const video of this.#videos.values()) video.muted = this.#muted;
    this.#syncSoundIcons();
  }

  #toggleSound() {
    bus.publish(MUTED_TOPIC, !this.#muted);
  }

  #syncSoundIcons() {
    const state = this.#muted ? 'muted' : 'unmuted';
    for (const toggle of this.#soundToggles.values()) {
      toggle.setIconState?.(state);
    }
  }

  #initSwiper() {
    this.#swiper = new Swiper(this.#viewport, {
      slidesPerView: 'auto',
      spaceBetween: this.#readLength('--tiktok-videos-gap'),
      slidesOffsetBefore: this.#readLength('--grid-margin'),
      slidesOffsetAfter: this.#readLength('--grid-margin'),
      watchOverflow: true,
      watchSlidesProgress: true,
      speed: this.#slideSpeed(),
      on: {
        afterInit: instance => {
          this.#syncEdges();
          this.#syncInert(instance);
        },
        slideChange: () => this.#onSlideChange(),
        transitionEnd: () => this.#stopHiddenOwner(),
        click: (instance, event) => this.#onCardClick(event),
        progress: instance => this.#syncInert(instance),
        fromEdge: () => this.#syncEdges(),
        reachBeginning: () => this.#syncEdges(),
        reachEnd: () => this.#syncEdges(),
        lock: () => this.#syncEdges(),
        unlock: () => this.#syncEdges(),
        resize: instance => this.#onSwiperResize(instance)
      }
    });
    this.cleanup(() => this.#swiper?.destroy(true, true));
  }

  #slideSpeed() {
    return this.#reduceMq.matches ? 0 : SLIDE_SPEED;
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
  }

  #onSwiperResize(instance) {
    const offset = this.#readLength('--grid-margin');
    if (instance.params.slidesOffsetAfter !== offset) {
      instance.params.slidesOffsetBefore = offset;
      instance.params.slidesOffsetAfter = offset;
      instance.update();
    }
    this.#syncEdges();
    this.#syncInert(instance);
  }

  #syncInert(instance) {
    const visibleClass = instance.params.slideVisibleClass;
    for (const slide of instance.slides) {
      slide.toggleAttribute('inert', !slide.classList.contains(visibleClass));
    }
  }

  #readLength(name) {
    const raw = getComputedStyle(this.#viewport).getPropertyValue(name);
    return parseFloat(raw) || 0;
  }

  #step(direction) {
    if (!this.#swiper || this.#swiper.isLocked) return;
    if (direction > 0) this.#swiper.slideNext();
    else this.#swiper.slidePrev();
  }

  #onKeydown(event) {
    if (event.target !== this.#viewport) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#swiper || this.#swiper.isLocked) return;
    event.preventDefault();
    const forward = this.#swiper.rtlTranslate
      ? event.key === 'ArrowLeft'
      : event.key === 'ArrowRight';
    this.#step(forward ? 1 : -1);
  }

  #syncEdges() {
    if (!this.#bound || this.#destroyed || !this.#swiper) return;
    const locked = this.#swiper.isLocked;
    this.toggleAttribute('data-no-scroll', locked);
    this.#prev?.setDisabled(locked || this.#swiper.isBeginning);
    this.#next?.setDisabled(locked || this.#swiper.isEnd);
  }

  #onCardClick(event) {
    if (event.target.closest(TOGGLE_TAG)) return;
    const card = this.#cardOf(event.target);
    if (card) this.#togglePlay(card);
  }

  #onSlideChange() {
    this.#syncEdges();
    this.#stopHiddenOwner();
  }

  #stopHiddenOwner() {
    const card = this.#cardsById.get(this.#owner);
    if (!card || !this.#swiper) return;
    if (card.classList.contains(this.#swiper.params.slideVisibleClass)) return;
    this.#stop(card);
  }

  #onSectionVisible(entries) {
    const entry = entries[entries.length - 1];
    if (!entry || entry.isIntersecting) return;
    this.#stopAll();
  }

  #cardOf(node) {
    return node.closest('[data-card]');
  }
}

if (!customElements.get('tiktok-videos')) {
  customElements.define('tiktok-videos', TiktokVideos);
}
